import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  BookingConfirmResponseInterface,
  BookingStartBodyInterface,
  BookingStartInterface,
  BookingInitResponseInterface,
  MetaInterface,
  BookingInterface,
} from '@sport-activities/sdk';
import { BehaviorSubject, Observable } from 'rxjs';
import { BmsTermsInterface } from '~/checkout/components/pages/payment/onepay/payment-onepay.component';
import { PromoCodeInterface } from '~/checkout/components/pages/payment/promo-code/payment-promo-code.interface';

import { CheckoutItemInterface } from '~/checkout/interfaces/checkout-item.interface';
import { ACTIVITIES_BASE_URL } from '~/core/core.module';
import { InvoiceUrlInterface } from '~/organizer/payout-invoices/interfaces/payout-invoice.interface';
import { QrCodeBookingInterface } from '~/organizer/shared/services/qr-code/interfaces/qr-code-interface';

import { ToggleService } from '~/toggles/services/toggle.service';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';

export interface CustomBookingStartResponseInterface {
  bmsTerms?: BmsTermsInterface;
  bookingId: string;
  paymentIntentId?: string;
  paymentIntentClientSecret?: string;
  status: string;
}

@Injectable({
  providedIn: 'root',
})
export class BookingService {
  public readonly toggleNameEnum = ToggleNameEnum;
  private readonly bookingId = new BehaviorSubject<string | null>(null);
  bookingId$ = this.bookingId.asObservable();

  constructor(
    private readonly toggleService: ToggleService,
    private readonly httpClient: HttpClient,
    @Inject(ACTIVITIES_BASE_URL) private readonly activitiesUrl: string
  ) {}

  public setBookingId(bookingId: string): void {
    this.bookingId.next(bookingId);
  }

  public getBookingId(): string | null {
    return this.bookingId.getValue();
  }

  public get isPromoCodeEnabled(): boolean {
    return this.toggleService.isEnabled(this.toggleNameEnum.PromoCode);
  }

  private buildAttendeesResponse(
    checkoutItem: CheckoutItemInterface,
    key: 'adults' | 'children'
  ) {
    return checkoutItem[key].map((attendees) => {
      const timeslot = checkoutItem?.timeslot;
      return {
        firstName: attendees.firstname.trim(),
        lastName: attendees.lastname.trim(),
        age: +attendees.age || 99,
        offerIdentifier:
          checkoutItem?.defaultOffer || timeslot?.offers[0]?.identifier,
      };
    });
  }

  private buildItemsResponse(
    checkoutItemCollection: CheckoutItemInterface[],
    promoCodeData?: PromoCodeInterface
  ) {
    let payload: BookingStartBodyInterface = {
      items: checkoutItemCollection.map((item) => ({
        sku: item?.timeslot?.identifier,
        meta: item?.meta,
        attendees: [
          ...this.buildAttendeesResponse(item, 'adults'),
          ...this.buildAttendeesResponse(item, 'children'),
        ],
      })),
    };

    if (this.isPromoCodeEnabled && promoCodeData) {
      payload = {
        ...payload,
        code: promoCodeData?.code,
      };
    }
    return payload;
  }

  public confirm(
    bookingId: string
  ): Observable<BookingConfirmResponseInterface> {
    return this.httpClient.post<BookingConfirmResponseInterface>(
      `${this.activitiesUrl}/booking/${bookingId}/confirm`,
      {}
    );
  }

  public init(
    body: CheckoutItemInterface[],
    redirectionUrl: string,
    promoCodeResponse?: PromoCodeInterface
  ) {
    return this.httpClient.post<BookingInitResponseInterface>(
      `${this.activitiesUrl}/booking/init`,
      {
        ...this.buildItemsResponse(
          body,
          this.isPromoCodeEnabled ? promoCodeResponse : null
        ),
        callback: {
          redirection: redirectionUrl,
        },
      }
    );
  }

  public getStatus(bookingId, paymentIntent): Observable<{ status: string }> {
    return this.httpClient.get<{ status: string }>(
      `${this.activitiesUrl}/booking/${bookingId}/validate?payment_intent=${paymentIntent}`
    );
  }

  public getBookingItemId(bookingId: string) {
    return this.httpClient.get<BookingInterface>(
      `${this.activitiesUrl}/booking/${bookingId}`,
      {}
    );
  }

  public refundItem(bookingItemId: string): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.activitiesUrl}/booking-items/${bookingItemId}`
    );
  }

  public downloadInvoice(
    bookingItemId: string
  ): Observable<InvoiceUrlInterface> {
    return this.httpClient.get<InvoiceUrlInterface>(
      `${this.activitiesUrl}/booking-items/${bookingItemId}/invoice-url`
    );
  }

  public getQrCode(bookingItemId: string): Observable<any> {
    return this.httpClient.get<QrCodeBookingInterface>(
      `${this.activitiesUrl}/booking-items/${bookingItemId}/qr-code`
    );
  }

  public getBookingItems(bookingItemId: string): Observable<any> {
    return this.httpClient.get<QrCodeBookingInterface>(
      `${this.activitiesUrl}/booking-items/${bookingItemId}`
    );
  }

  public start(
    body: CheckoutItemInterface[],
    promoCodeResponse?: PromoCodeInterface,
    meta?: MetaInterface
  ): Observable<CustomBookingStartResponseInterface> {
    const paymentTokenPayload = {};

    const bookignStartPayload: BookingStartBodyInterface = {
      ...this.buildItemsResponse(
        body,
        this.isPromoCodeEnabled ? promoCodeResponse : null
      ),
      ...paymentTokenPayload,
      meta,
    };

    return this.httpClient.post<BookingStartInterface>(
      `${this.activitiesUrl}/booking/start`,
      bookignStartPayload
    );
  }

  public addOneFFBooking(
    bookingId: string
  ): Observable<BookingConfirmResponseInterface> {
    return this.httpClient.post<BookingConfirmResponseInterface>(
      `${this.activitiesUrl}/booking/${bookingId}/oneff`,
      {}
    );
  }

  public deleteOneFFBooking(
    bookingId: string
  ): Observable<BookingConfirmResponseInterface> {
    return this.httpClient.delete<BookingConfirmResponseInterface>(
      `${this.activitiesUrl}/booking/${bookingId}/oneff`,
      {}
    );
  }
}
